<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        ></v-progress-circular>
        <v-card v-else max-width="650px" width="650px">
          <v-card-title class="ml-4">
            <span class="headline" v-if="editView">{{
              $t("tasks.tasksTemplatesAddEdit.editChecklist")
            }}</span>
            <span class="headline" v-else>{{
              $t("tasks.tasksTemplatesAddEdit.newChecklist")
            }}</span>
            <v-btn
              v-if="canUseKnowledgeBaseBtn"
              @click="handleHelp"
              icon
              color="primary"
            >
              <v-icon class="mx-2">mdi-help-circle</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-title>

          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="12">
                <template v-for="(checklist, index) in editedItem.checklists">
                  <checklist-add-edit
                    :key="checklist.vueId"
                    :professional="editedItem.crm_partner_uuid"
                    :checklist-index="index"
                    :checklist="checklist"
                    @update:checklist="updateChecklist"
                    :disabled="checkFinishedTask"
                    :boat-disabled="true"
                    :show-boat="false"
                    :toolbar-disabled="true"
                    :show-servicing-partner="false"
                  ></checklist-add-edit>
                </template>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <template>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="close()">{{
                $t("tasks.tasksTemplatesAddEdit.cancel")
              }}</v-btn>
              <v-btn color="primary" text class="mr-2" @click="saveTask">{{
                $t("tasks.tasksTemplatesAddEdit.save")
              }}</v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-layout>
    </v-container>
    <help-dialog
      :dialog="helpDialog"
      @close="closeHelpDialog"
      v-if="helpDialog.active"
    ></help-dialog>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar";
import { mapGetters, mapState } from "vuex";
import ChecklistAddEdit from "@/components/checklists/ChecklistAddEdit.vue";
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";

export default {
  components: {
    ChecklistAddEdit,
    HelpDialog,
  },
  data: () => ({
    canUseKnowledgeBaseBtn: false,
    helpDialog: { location: null, active: false },
    editView: false,
    loading: true,
    editedItem: {
      name: "",
      checklists: [],
    },
  }),

  computed: {
    ...mapGetters(["getIframeStatus", "getMe"]),
    getStatus() {
      return this.statusOptions.find(
        (item) => item.status === this.editedItem.status
      ).name;
    },
    checkFinishedTask() {
      return !!this.editedItem.completed;
    },
    getDuration() {
      let duration = this.durations.find(
        (item) => item.value === this.editedItem.duration
      );
      return typeof duration === "undefined"
        ? this.editedItem.duration
        : duration.duration;
    },

    modelPresent() {
      return !!this.editedItem.crm_boat;
    },
  },

  created() {
    this.canUseKnowledgeBaseBtn = constraints.canUseKnowledgeBaseBtn("tasks");
    this.getView();
    if (this.editView) {
      this.loadTask();
    } else {
      this.editedItem.checklists = [this.setNewBoatTemplate()];
      this.loading = false;
    }
  },
  watch: {
    $route(newValue, oldValue) {
      this.getView();
    },
  },

  methods: {
    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },
    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location = "tasks";
    },

    updateChecklist(object) {
      const { checklist, index } = object;
      this.$set(this.editedItem.checklists, index, checklist);
    },

    setNewBoatTemplate(boat = null, subtasks = []) {
      return {
        vueId: this.editedItem.checklists.length,
        crm_boat: boat,
        subtasks: subtasks,
        crm_boat_uuid: null,
        name: `${this.$t("tasks.tasksTemplatesAddEdit.checklist")} ${
          this.editedItem.checklists.length + 1
        }`,
      };
    },

    getView() {
      this.editView = this.$route.name === "tasksTemplatesEdit";
    },

    loadTask() {
      return new Promise((resolve, reject) => {
        api.getTemlate(this.$route.params.id).then((resp) => {
          this.editedItem = resp.data;
          this.editedItem.priority = Number(this.editedItem.priority);
          this.loading = false;
          resolve();
        });
      });
    },

    close() {
      this.$router.push({
        name: "tasksTemplate",
      });
    },

    previewTask(taskUuid) {
      this.$router
        .pushAsync({
          name: "tasksTemplate",
        })
        .then((resp) => {
          this.loading = false;
        });
    },

    saveTask() {
      this.loading = true;
      this.editedItem.crm_group_uuid = this.getMe.crm_groups[0].uuid;
      this.editedItem.name = this.editedItem.checklists[0].name;
      if (this.editView) {
        //false parameter is here for disabling error handling
        api
          .updateTemplate(this.editedItem.uuid, this.editedItem, false)
          .then((resp) => {
            snackbar.success(
              this.$t("utils.snackbar.success.successfullySaved")
            );
            this.previewTask(resp.data.uuid);
          })
          .catch((err) => {
            this.handleError(err.response);
            this.loading = false;
          });
      } else {
        //false parameter is here for disabling error handling
        api
          .createTemplate(this.editedItem, false)
          .then((resp) => {
            snackbar.success(
              this.$t("utils.snackbar.success.successfullySaved")
            );
            this.previewTask(resp.data.uuid);
          })
          .catch((err) => {
            this.handleError(err.response);
            this.loading = false;
          });
      }
    },

    handleError(err) {
      if (err) {
        snackbar.error(err.response);
      } else {
        snackbar.error();
      }
    },
  },
};
</script>
<style scoped></style>
